import * as React from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { cn } from "@/lib/utils";
import { BrandCard, type BrandCardProps } from "./BrandCard";

/**
 * AnimatedCard Component - Light Theme Editorial Design
 *
 * Interactive card with hover animations:
 * - Lift effect on hover
 * - Tilt effect (3D)
 * - Subtle shadow enhancement
 * - Scale animation
 */

export interface AnimatedCardProps extends BrandCardProps {
  /**
   * Enable tilt effect
   */
  enableTilt?: boolean;

  /**
   * Enable scale on hover
   */
  enableScale?: boolean;

  /**
   * Enable lift on hover
   */
  enableLift?: boolean;

  /**
   * Animation duration
   */
  duration?: number;

  /**
   * Click handler
   */
  onClick?: () => void;
}

export const AnimatedCard = React.forwardRef<HTMLDivElement, AnimatedCardProps>(
  (
    {
      children,
      className,
      enableTilt = false,
      enableScale = true,
      enableLift = true,
      duration = 0.3,
      onClick,
      hoverable = true,
      glow = "subtle",
      ...props
    },
    ref,
  ) => {
    const cardRef = React.useRef<HTMLDivElement>(null);

    // Mouse position
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    // Spring animations for smooth movement
    const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [5, -5]), {
      stiffness: 300,
      damping: 30,
    });
    const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-5, 5]), {
      stiffness: 300,
      damping: 30,
    });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!enableTilt || !cardRef.current) return;

      const rect = cardRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const percentX = (e.clientX - centerX) / (rect.width / 2);
      const percentY = (e.clientY - centerY) / (rect.height / 2);

      mouseX.set(percentX);
      mouseY.set(percentY);
    };

    const handleMouseLeave = () => {
      if (!enableTilt) return;
      mouseX.set(0);
      mouseY.set(0);
    };

    return (
      <motion.div
        ref={cardRef}
        className={cn(onClick && "cursor-pointer")}
        style={
          enableTilt
            ? {
                rotateX,
                rotateY,
                transformStyle: "preserve-3d",
              }
            : undefined
        }
        whileHover={
          enableScale
            ? {
                scale: 1.02,
                transition: { duration },
              }
            : undefined
        }
        whileTap={
          onClick
            ? {
                scale: 0.98,
                transition: { duration: 0.1 },
              }
            : undefined
        }
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={onClick}
      >
        <BrandCard
          ref={ref}
          className={cn(
            "transition-all duration-300",
            enableLift && "hover:-translate-y-1 hover:shadow-lg",
            className,
          )}
          hoverable={hoverable}
          glow={glow}
          {...props}
        >
          {children}
        </BrandCard>
      </motion.div>
    );
  },
);

AnimatedCard.displayName = "AnimatedCard";

/**
 * HoverGlowCard - Card with enhanced shadow on hover
 */
export interface HoverGlowCardProps extends BrandCardProps {
  /**
   * Glow color
   */
  glowColor?: "gold" | "emerald" | "custom";

  /**
   * Custom glow color (CSS color)
   */
  customGlowColor?: string;
}

export const HoverGlowCard = React.forwardRef<
  HTMLDivElement,
  HoverGlowCardProps
>(
  (
    { children, className, glowColor, customGlowColor, ...props },
    ref,
  ) => {
    return (
      <motion.div
        className="relative"
        whileHover={{
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
          transition: { duration: 0.3 },
        }}
      >
        <BrandCard ref={ref} className={className} glow="strong" {...props}>
          {children}
        </BrandCard>
      </motion.div>
    );
  },
);

HoverGlowCard.displayName = "HoverGlowCard";

/**
 * FlipCard - Card with flip animation
 */
export interface FlipCardProps {
  /**
   * Front content
   */
  front: React.ReactNode;

  /**
   * Back content
   */
  back: React.ReactNode;

  /**
   * Container class name
   */
  className?: string;

  /**
   * Card props for both sides
   */
  cardProps?: BrandCardProps;
}

export const FlipCard = ({
  front,
  back,
  className,
  cardProps,
}: FlipCardProps) => {
  const [isFlipped, setIsFlipped] = React.useState(false);

  return (
    <div className={cn("perspective-1000", className)}>
      <motion.div
        className="relative w-full h-full"
        style={{ transformStyle: "preserve-3d" }}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        onClick={() => setIsFlipped(!isFlipped)}
      >
        {/* Front */}
        <div
          className={cn(
            "absolute inset-0 backface-hidden",
            isFlipped && "pointer-events-none",
          )}
          style={{ backfaceVisibility: "hidden" }}
        >
          <BrandCard hoverable {...cardProps}>
            {front}
          </BrandCard>
        </div>

        {/* Back */}
        <div
          className={cn(
            "absolute inset-0 backface-hidden",
            !isFlipped && "pointer-events-none",
          )}
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <BrandCard hoverable {...cardProps}>
            {back}
          </BrandCard>
        </div>
      </motion.div>
    </div>
  );
};

FlipCard.displayName = "FlipCard";

/**
 * PulsingCard - Card with pulsing animation
 */
export interface PulsingCardProps extends BrandCardProps {
  /**
   * Pulse speed (seconds per cycle)
   */
  pulseSpeed?: number;
}

export const PulsingCard = React.forwardRef<HTMLDivElement, PulsingCardProps>(
  ({ children, className, pulseSpeed = 2, ...props }, ref) => {
    return (
      <motion.div
        animate={{
          boxShadow: [
            "0 0 20px rgba(15, 110, 86, 0.1)",
            "0 0 40px rgba(15, 110, 86, 0.2)",
            "0 0 20px rgba(15, 110, 86, 0.1)",
          ],
        }}
        transition={{
          duration: pulseSpeed,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <BrandCard ref={ref} className={className} {...props}>
          {children}
        </BrandCard>
      </motion.div>
    );
  },
);

PulsingCard.displayName = "PulsingCard";
